import {
  PRINTED_STAMP_HEIGHT_PT,
  SIGNATURE_BOX_HEIGHT_PT,
  SIGNATURE_BOX_WIDTH_PT,
  SignatureBoxRect,
  fitSignatureBoxInPage,
} from './signature-box.util';

/**
 * Where a Pre-Defined / Default signature goes, for every flow (CAT Execution,
 * Multiple e-Stamp, CAT Edit, CAT Compose, e-Sign, bulk upload). Keeping one copy
 * is what makes the same position land in the same spot, at the same size, for
 * Aadhaar, DSC and Electronic alike; separate copies had drifted apart.
 *
 * Coordinates are PDF points. Electronic rects are top-down (y from the top of
 * the page); Aadhaar/DSC rects are bottom-up (PDF), as each signing service
 * expects.
 */

export type SignMethod = 'ELECTRONIC' | 'AADHAAR' | 'DSC';

export const STANDARD_PAGE_WIDTH_PT = 595;
export const STANDARD_PAGE_HEIGHT_PT = 842;

const BOTTOM_GRID_COLUMNS: { [base: string]: number } = {
  BOTTOM_LEFT: 0,
  BOTTOM_LEFT_MIDDLE: 1,
  BOTTOM_CENTRE: 2,
  BOTTOM_CENTER: 2,
  BOTTOM_RIGHT_MIDDLE: 3,
  BOTTOM_RIGHT: 4,
};

const SINGLE_POSITIONS = [
  'TOP_LEFT',
  'TOP_CENTRE',
  'TOP_CENTER',
  'TOP_RIGHT',
  'MIDDLE_LEFT',
  'MIDDLE_RIGHT',
];

// Gap between the two bottom rows, matching the column gutter.
const BOTTOM_GRID_ROW_GAP_PT = 5;

/** Column/row of a bottom-grid position (BOTTOM_* = row 0, BOTTOM_*_2 = row 1). */
export function getBottomGridPosition(signPosition: string): { col: number; row: number } | null {
  const position = (signPosition || '').toUpperCase();
  const isRow2 = position.endsWith('_2');
  const base = isRow2 ? position.slice(0, -2) : position;
  if (!(base in BOTTOM_GRID_COLUMNS)) {
    return null;
  }
  return { col: BOTTOM_GRID_COLUMNS[base], row: isRow2 ? 1 : 0 };
}

/**
 * True for the positions this file places. Anything else (client-specific spots
 * such as BAILEE_1) keeps its own fixed coordinates in the flow that defines it.
 */
export function isStandardSignPosition(signPosition: string): boolean {
  const position = (signPosition || '').toUpperCase();
  return !!getBottomGridPosition(position) || SINGLE_POSITIONS.includes(position);
}

function getGridColumnBand(pageWidth: number, colIndex: number): { left: number; width: number } {
  const columns = 5;
  const marginX = Math.max(5, Math.round(pageWidth * 0.015));
  const gutterX = Math.max(4, Math.round(pageWidth * 0.008));
  const width = Math.round((pageWidth - 2 * marginX - (columns - 1) * gutterX) / columns);
  return { left: marginX + colIndex * (width + gutterX), width };
}

// Rows stack up from the bottom margin by the height a stamp actually prints at
// (PRINTED_STAMP_HEIGHT_PT, the lower part of its box for every method), so the
// second row's signature sits just above the first row's (A4 boxes: row 1 at
// 5-55pt, row 2 at 40-90pt from the bottom; printed stamps at 5-35pt and
// 40-70pt). The boxes overlap only in their empty top part.
function getGridRowBottom(pageHeight: number, rowIndex: number): number {
  const marginY = Math.max(4, Math.round(pageHeight * 0.006));
  return marginY + rowIndex * (PRINTED_STAMP_HEIGHT_PT + BOTTOM_GRID_ROW_GAP_PT);
}

/** Bottom-grid box in bottom-up (PDF) coordinates, same size for every method. */
function getBottomGridRect(col: number, row: number, pageWidth: number, pageHeight: number): SignatureBoxRect {
  const colBand = getGridColumnBand(pageWidth, col);
  const x1 = Math.round(colBand.left + (colBand.width - SIGNATURE_BOX_WIDTH_PT) / 2);
  const y1 = getGridRowBottom(pageHeight, row);
  return { x1, x2: x1 + SIGNATURE_BOX_WIDTH_PT, y1, y2: y1 + SIGNATURE_BOX_HEIGHT_PT };
}

// TOP_* / MIDDLE_* for Aadhaar/DSC, bottom-up coordinates.
function getAadhaarDscSinglePositionRect(
  signPosition: string,
  pageWidth: number,
  pageHeight: number,
): SignatureBoxRect {
  const isSmallPage = pageWidth < 500;
  const signatureWidth = SIGNATURE_BOX_WIDTH_PT;
  const signatureHeight = SIGNATURE_BOX_HEIGHT_PT;
  const margin = Math.round(
    isSmallPage ? Math.min(3, pageWidth * 0.02) : Math.min(5, pageWidth * 0.01),
  );

  const topY = pageHeight - signatureHeight - margin;
  const middleY = Math.max(margin, Math.round(pageHeight / 2 - signatureHeight / 2));
  const centreX = Math.round(pageWidth / 2 - signatureWidth / 2);
  const rightX = pageWidth - signatureWidth - margin;

  let x1: number;
  let y1: number;
  switch (signPosition) {
    case 'TOP_LEFT':
      x1 = margin;
      y1 = topY;
      break;
    case 'TOP_CENTER':
    case 'TOP_CENTRE':
      x1 = centreX;
      y1 = topY;
      break;
    case 'TOP_RIGHT':
      x1 = rightX;
      y1 = topY;
      break;
    case 'MIDDLE_LEFT':
      x1 = margin;
      y1 = middleY;
      break;
    case 'MIDDLE_RIGHT':
      x1 = rightX;
      y1 = middleY;
      break;
    default:
      x1 = margin;
      y1 = margin;
  }
  return fitSignatureBoxInPage(x1, y1, signatureWidth, signatureHeight, pageWidth, pageHeight);
}

// TOP_* / MIDDLE_* for Electronic, top-down coordinates.
function getElectronicSinglePositionRect(
  signPosition: string,
  pageWidth: number,
  pageHeight: number,
): SignatureBoxRect {
  const signWidth = SIGNATURE_BOX_WIDTH_PT;
  const signHeight = SIGNATURE_BOX_HEIGHT_PT;
  const margin = Math.round(pageWidth * 0.01);

  let x1 = margin;
  let y1 = margin;
  if (signPosition.startsWith('MIDDLE')) {
    y1 = Math.round(pageHeight / 2 - signHeight / 2);
  }
  switch (signPosition) {
    case 'TOP_CENTRE':
    case 'TOP_CENTER':
      x1 = Math.round(pageWidth / 2 - signWidth / 2);
      break;
    case 'TOP_RIGHT':
    case 'MIDDLE_RIGHT':
      x1 = pageWidth - (margin + signWidth);
      break;
  }
  return fitSignatureBoxInPage(x1, y1, signWidth, signHeight, pageWidth, pageHeight);
}

/**
 * The signature rect for a standard position, in the coordinate system the
 * method's signing service uses (Electronic top-down, Aadhaar/DSC bottom-up).
 */
export function getStandardSignatureRect(
  method: string,
  signPosition: string,
  pageWidth: number = STANDARD_PAGE_WIDTH_PT,
  pageHeight: number = STANDARD_PAGE_HEIGHT_PT,
): SignatureBoxRect {
  const signMethod = (method || '').toUpperCase().trim();
  const position = (signPosition || '').toUpperCase();
  const gridPos = getBottomGridPosition(position);

  if (gridPos) {
    const grid = getBottomGridRect(gridPos.col, gridPos.row, pageWidth, pageHeight);
    // Kept on the page by moving it, never by trimming it.
    const rect = fitSignatureBoxInPage(
      grid.x1,
      grid.y1,
      SIGNATURE_BOX_WIDTH_PT,
      SIGNATURE_BOX_HEIGHT_PT,
      pageWidth,
      pageHeight,
    );
    if (signMethod === 'AADHAAR' || signMethod === 'DSC') {
      return rect;
    }
    // Electronic: same spot, flipped to top-down.
    return { x1: rect.x1, x2: rect.x2, y1: pageHeight - rect.y2, y2: pageHeight - rect.y1 };
  }

  return signMethod === 'AADHAAR' || signMethod === 'DSC'
    ? getAadhaarDscSinglePositionRect(position, pageWidth, pageHeight)
    : getElectronicSinglePositionRect(position, pageWidth, pageHeight);
}
